'use client';

import { c as _c } from "react/compiler-runtime";
import { Spinner } from '@common/components';
import { Pagination } from '@common/components/molecule/Pagination';
import { useToast } from '@common/hooks';
import { preventSkipToken } from '@common/utils/preventSkipToken';
import { cn, CustomError, isEnumValue } from '@core/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { debounce } from 'es-toolkit';
import { usePathname } from 'next/navigation';
import { Suspense, useEffect, useMemo, useState } from 'react';
import CountUp from 'react-countup';
import { ErrorBoundary } from 'react-error-boundary';
import { useInView } from 'react-intersection-observer';
import type { COMMENT_TYPE } from '@/api';
import { AuthInfiniteQueryOptions, AuthQueryOptions, COMMENT_SORT_TYPE, LIKE_TYPE, UnauthInfiniteQueryOptions, UnauthQueryOptions } from '@/api';
import { type Comment, type CommentData, type CommentListPage, selectCommentList, toContentIdQuery } from '@/api/getCommentList';
import { BREAKPOINTS, ROUTES, SELECTOR } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useLike } from '@/hooks/useLike';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAuthStore } from '@/stores/AuthStore';
import { isBot } from '@/utils/isBot';
import { replaceSortTypeInUrl } from './_hooks/useCommentThreadNavigation';
import { CommentCard } from './CommentCard';
import { CommentSkeletons } from './CommentSkeleton';
import { PointEmptySection } from './PointEmptySection';
interface CommentCardsProps {
  commentType: COMMENT_TYPE;
  isLogin: boolean;
  rootCommentId?: number;
  contentId: number | string;
  sortType: COMMENT_SORT_TYPE;
  initialPageData?: CommentListPage;
  /** 작성 성공마다 증가하는 신호. 데스크톱 페이지네이션을 1페이지로 되돌릴 때 쓴다. */
  postNonce?: number;
  onChangeSortType?: (sortType: COMMENT_SORT_TYPE) => void;
  onClickReplyButton?: (comment: Comment, rootCommentId: number, parentCommentId: number) => void;
  onClickEditButton?: (comment: Comment, isRoot: boolean) => void;
  onDeleteSuccess?: (deletedCommentId: number) => void;
  onClickPickButton?: (commentId: number) => void;
  onChangeCommentData?: (data: CommentData) => void;
  className?: string;
  hasPickButton?: boolean;
}
const SIZE = 20;
const INITIAL_PAGE = 1;
interface CommentListHeaderProps {
  isLogin: boolean;
  sortType: COMMENT_SORT_TYPE;
  visibleTotalCount: number;
  onChangeSortType?: (sortType: COMMENT_SORT_TYPE) => void;
}
const CommentListHeader = t0 => {
  const $ = _c(31);
  const {
    isLogin,
    sortType,
    visibleTotalCount,
    onChangeSortType
  } = t0;
  const {
    toast
  } = useToast();
  const handleChangeSortType = onChangeSortType ?? replaceSortTypeInUrl;
  let t1;
  if ($[0] !== isLogin || $[1] !== toast) {
    t1 = () => {
      if (!isLogin) {
        toast({
          message: "\uB85C\uADF8\uC778 \uD6C4 \uC774\uC6A9\uD574\uC8FC\uC138\uC694."
        });
        return true;
      }
      return false;
    };
    $[0] = isLogin;
    $[1] = toast;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const requireLoginToast = t1;
  let t2;
  if ($[3] !== isLogin || $[4] !== visibleTotalCount) {
    t2 = isLogin ? <>댓글 <CountUp preserveValue={true} duration={1} end={visibleTotalCount} /></> : null;
    $[3] = isLogin;
    $[4] = visibleTotalCount;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <strong className="font-600 text-Gray-900 typo-body4">{t2}</strong>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = sortType === COMMENT_SORT_TYPE.LATEST;
  let t5;
  if ($[8] !== t4) {
    t5 = cn({
      "text-Gray-800": t4
    });
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== handleChangeSortType || $[11] !== requireLoginToast) {
    t6 = () => {
      if (requireLoginToast()) {
        return;
      }
      handleChangeSortType(COMMENT_SORT_TYPE.LATEST);
    };
    $[10] = handleChangeSortType;
    $[11] = requireLoginToast;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <button className={t5} type="button" onClick={t6}>최신순</button>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="typo-caption1">|</span>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = sortType === COMMENT_SORT_TYPE.POPULARITY;
  let t10;
  if ($[17] !== t9) {
    t10 = cn({
      "text-Gray-800": t9
    });
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== handleChangeSortType || $[20] !== requireLoginToast) {
    t11 = () => {
      if (requireLoginToast()) {
        return;
      }
      handleChangeSortType(COMMENT_SORT_TYPE.POPULARITY);
    };
    $[19] = handleChangeSortType;
    $[20] = requireLoginToast;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <button className={t10} type="button" onClick={t11}>인기순</button>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t7) {
    t13 = <div className="flex items-center gap-8 font-500 text-Gray-500 typo-body4">{t7}{t8}{t12}</div>;
    $[25] = t12;
    $[26] = t7;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13 || $[29] !== t3) {
    t14 = <header className="mb-16 flex items-center justify-between">{t3}{t13}</header>;
    $[28] = t13;
    $[29] = t3;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
};
interface CommentItemsProps {
  commentList: CommentData['commentList'];
  isLogin: boolean;
  hasPickButton?: boolean;
  isInitialChildrenOpened: boolean;
  onLike: (commentId: number, isLike: boolean) => void;
  onClickReplyButton?: (comment: Comment, rootCommentId: number, parentCommentId: number) => void;
  onClickEditButton?: (comment: Comment, isRoot: boolean) => void;
  onDeleteSuccess?: (deletedCommentId: number) => void;
  onClickPickButton?: (commentId: number) => void;
}
const CommentItems = t0 => {
  const $ = _c(22);
  const {
    commentList,
    isLogin,
    hasPickButton,
    isInitialChildrenOpened,
    onLike,
    onClickReplyButton,
    onClickEditButton,
    onDeleteSuccess,
    onClickPickButton
  } = t0;
  if (commentList.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <PointEmptySection mainMessage={"\uC544\uC9C1 \uB313\uAE00\uC774 \uC5C6\uC5B4\uC694"} subMessage={"\uC9C0\uAE08 \uCCAB \uB313\uAE00\uC744 \uB0A8\uACA8\uBCF4\uC138\uC694!"} />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== commentList || $[2] !== hasPickButton || $[3] !== isInitialChildrenOpened || $[4] !== isLogin || $[5] !== onClickEditButton || $[6] !== onClickPickButton || $[7] !== onClickReplyButton || $[8] !== onDeleteSuccess || $[9] !== onLike) {
    let t2;
    if ($[11] !== hasPickButton || $[12] !== isInitialChildrenOpened || $[13] !== isLogin || $[14] !== onClickEditButton || $[15] !== onClickPickButton || $[16] !== onClickReplyButton || $[17] !== onDeleteSuccess || $[18] !== onLike) {
      t2 = comment => {
        const {
          commentId
        } = comment;
        return <li key={commentId}><CommentCard comment={comment} hasMenu={isLogin} hasPickButton={!comment.isMine && comment.isWelloHelper && hasPickButton} initialChildrenOpened={isInitialChildrenOpened} onDeleteSuccess={onDeleteSuccess} onClickEditButton={() => {
            onClickEditButton?.(comment, true);
          }} onClickLikeButton={() => onLike(commentId, comment.isMyThumbUp)} onClickPickButton={() => onClickPickButton?.(commentId)} onClickReplyButton={() => {
            onClickReplyButton?.(comment, commentId, commentId);
          }}>{comment.childCommentList?.length ? <ul className="ml-52 mt-16 flex flex-col gap-8">{comment.childCommentList.map(childrenComment => {
                const {
                  commentId: childrenCommentId
                } = childrenComment;
                return <li key={childrenCommentId}><CommentCard comment={childrenComment} hasMenu={isLogin} onDeleteSuccess={onDeleteSuccess} onClickEditButton={() => {
                    onClickEditButton?.(childrenComment, false);
                  }} onClickLikeButton={() => onLike(childrenCommentId, childrenComment.isMyThumbUp)} onClickReplyButton={() => {
                    onClickReplyButton?.(childrenComment, commentId, childrenCommentId);
                  }} /></li>;
              })}</ul> : null}</CommentCard></li>;
      };
      $[11] = hasPickButton;
      $[12] = isInitialChildrenOpened;
      $[13] = isLogin;
      $[14] = onClickEditButton;
      $[15] = onClickPickButton;
      $[16] = onClickReplyButton;
      $[17] = onDeleteSuccess;
      $[18] = onLike;
      $[19] = t2;
    } else {
      t2 = $[19];
    }
    t1 = commentList.map(t2);
    $[1] = commentList;
    $[2] = hasPickButton;
    $[3] = isInitialChildrenOpened;
    $[4] = isLogin;
    $[5] = onClickEditButton;
    $[6] = onClickPickButton;
    $[7] = onClickReplyButton;
    $[8] = onDeleteSuccess;
    $[9] = onLike;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[20] !== t1) {
    t2 = <>{t1}</>;
    $[20] = t1;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  return t2;
};
const LoginRequiredSection = () => {
  const $ = _c(3);
  const router = useCustomRouter();
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname || $[1] !== router) {
    t0 = <PointEmptySection actionButton={{
      text: "\uD68C\uC6D0 \uAC00\uC785\uD558\uAE30",
      onClick: () => router.push(ROUTES.LOGIN.withSearchParams({
        searchParams: {
          redirect: pathname ?? ""
        }
      }))
    }} mainMessage={"\uB313\uAE00\uC744 \uD655\uC778\uD558\uB824\uBA74? \uD83D\uDC40"} subMessage={"\uB85C\uADF8\uC778 \uD6C4 \uBCFC \uC218 \uC788\uC5B4\uC694.\n\uD68C\uC6D0\uAC00\uC785 \uD6C4 \uC720\uC800\uB4E4\uC758 \uBC18\uC751\uC744 \uD655\uC778\uD574\uBCF4\uC138\uC694!"} />;
    $[0] = pathname;
    $[1] = router;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
const CommentCardsInfinite = t0 => {
  const $ = _c(63);
  const {
    isLogin,
    commentType,
    rootCommentId,
    contentId,
    sortType,
    initialPageData,
    onChangeSortType,
    onClickReplyButton,
    onClickEditButton,
    onDeleteSuccess,
    onClickPickButton,
    onChangeCommentData,
    className,
    hasPickButton
  } = t0;
  const userId = useAuthStore(_temp);
  let t1;
  if ($[0] !== commentType || $[1] !== contentId || $[2] !== isLogin || $[3] !== rootCommentId || $[4] !== sortType) {
    t1 = preventSkipToken((isLogin ? AuthInfiniteQueryOptions["get:/wello/v2/auth/wello-comment"] : UnauthInfiniteQueryOptions["get:/wello/v2/allows/wello-comment"])({
      query: {
        page: INITIAL_PAGE,
        commentTypeCd: commentType,
        commentSort: sortType,
        size: SIZE,
        parentCommentId: rootCommentId,
        ...toContentIdQuery(contentId)
      }
    }));
    $[0] = commentType;
    $[1] = contentId;
    $[2] = isLogin;
    $[3] = rootCommentId;
    $[4] = sortType;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = isBot();
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== initialPageData) {
    t3 = initialPageData ? {
      pages: [initialPageData],
      pageParams: [INITIAL_PAGE]
    } : undefined;
    $[7] = initialPageData;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== userId) {
    t4 = t5 => {
      const {
        pages
      } = t5;
      const lastCommentPage = selectCommentList(pages[pages.length - 1].context, userId);
      const flatCommentList = pages.flatMap(t6 => {
        const {
          context: context_0
        } = t6;
        return selectCommentList(context_0, userId).commentList;
      });
      const uniqueCommentList = flatCommentList.filter(_temp3);
      return {
        ...lastCommentPage,
        commentList: uniqueCommentList
      };
    };
    $[9] = userId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      ...t1,
      enabled: !t2,
      initialData: t3,
      staleTime: Number.POSITIVE_INFINITY,
      getNextPageParam: _temp2,
      select: t4,
      initialPageParam: INITIAL_PAGE,
      placeholderData: keepPreviousData
    };
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const commentListQuery = useInfiniteQuery(t5);
  let t6;
  if ($[15] !== commentListQuery.data) {
    t6 = commentListQuery.data ?? {};
    $[15] = commentListQuery.data;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const {
    commentList: t7,
    visibleTotalCount: t8
  } = t6;
  let t9;
  if ($[17] !== t7) {
    t9 = t7 === undefined ? [] : t7;
    $[17] = t7;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const commentList = t9;
  const visibleTotalCount = t8 === undefined ? 0 : t8;
  const {
    fetchNextPage
  } = commentListQuery;
  let t10;
  if ($[19] !== fetchNextPage) {
    t10 = debounce(fetchNextPage, 1000);
    $[19] = fetchNextPage;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const debouncedFetchNextPage = t10;
  let t11;
  if ($[21] !== debouncedFetchNextPage) {
    t11 = {
      onChange: inView => {
        if (inView) {
          debouncedFetchNextPage();
        }
      }
    };
    $[21] = debouncedFetchNextPage;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const {
    ref
  } = useInView(t11);
  const {
    like
  } = useLike();
  let t12;
  if ($[23] !== like) {
    t12 = (commentId, isLike) => {
      like({
        contentId: commentId,
        isLike,
        likeType: LIKE_TYPE.COMMENT
      });
    };
    $[23] = like;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const handleLike = t12;
  let t13;
  let t14;
  if ($[25] !== commentListQuery.data || $[26] !== onChangeCommentData) {
    t13 = () => {
      if (!onChangeCommentData || !commentListQuery.data) {
        return;
      }
      onChangeCommentData(commentListQuery.data);
    };
    t14 = [commentListQuery.data, onChangeCommentData];
    $[25] = commentListQuery.data;
    $[26] = onChangeCommentData;
    $[27] = t13;
    $[28] = t14;
  } else {
    t13 = $[27];
    t14 = $[28];
  }
  useEffect(t13, t14);
  const isRootComment = !rootCommentId;
  const isInitialChildrenOpened = commentList.length < 50;
  let t15;
  if ($[29] !== className) {
    t15 = cn("bg-Gray-white px-16", className);
    $[29] = className;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== isLogin || $[32] !== onChangeSortType || $[33] !== sortType || $[34] !== visibleTotalCount) {
    t16 = <CommentListHeader isLogin={isLogin} onChangeSortType={onChangeSortType} sortType={sortType} visibleTotalCount={visibleTotalCount} />;
    $[31] = isLogin;
    $[32] = onChangeSortType;
    $[33] = sortType;
    $[34] = visibleTotalCount;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== isLogin) {
    t17 = !isLogin ? <LoginRequiredSection /> : null;
    $[36] = isLogin;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const t18 = !isLogin;
  let t19;
  if ($[38] !== t18) {
    t19 = cn("flex flex-col gap-12 pb-12", {
      "invisible absolute size-0 overflow-hidden": t18
    });
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== commentList || $[41] !== handleLike || $[42] !== hasPickButton || $[43] !== isInitialChildrenOpened || $[44] !== isLogin || $[45] !== onClickEditButton || $[46] !== onClickPickButton || $[47] !== onClickReplyButton || $[48] !== onDeleteSuccess) {
    t20 = <CommentItems commentList={commentList} isLogin={isLogin} hasPickButton={hasPickButton} isInitialChildrenOpened={isInitialChildrenOpened} onDeleteSuccess={onDeleteSuccess} onLike={handleLike} onClickReplyButton={onClickReplyButton} onClickEditButton={onClickEditButton} onClickPickButton={onClickPickButton} />;
    $[40] = commentList;
    $[41] = handleLike;
    $[42] = hasPickButton;
    $[43] = isInitialChildrenOpened;
    $[44] = isLogin;
    $[45] = onClickEditButton;
    $[46] = onClickPickButton;
    $[47] = onClickReplyButton;
    $[48] = onDeleteSuccess;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== commentListQuery.hasNextPage || $[51] !== isRootComment || $[52] !== ref) {
    t21 = commentListQuery.hasNextPage && isRootComment ? <li ref={ref} className="flex-center"><Spinner /></li> : null;
    $[50] = commentListQuery.hasNextPage;
    $[51] = isRootComment;
    $[52] = ref;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t19 || $[55] !== t20 || $[56] !== t21) {
    t22 = <ul className={t19}>{t20}{t21}</ul>;
    $[54] = t19;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== t15 || $[59] !== t16 || $[60] !== t17 || $[61] !== t22) {
    t23 = <section className={t15} id={SELECTOR.COMMENT_LIST_TOP}>{t16}{t17}{t22}</section>;
    $[58] = t15;
    $[59] = t16;
    $[60] = t17;
    $[61] = t22;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  return t23;
};
const CommentCardsPaginated = t0 => {
  const $ = _c(58);
  const {
    isLogin,
    commentType,
    rootCommentId,
    contentId,
    sortType,
    initialPageData,
    postNonce,
    onChangeSortType,
    onClickReplyButton,
    onClickEditButton,
    onDeleteSuccess,
    onClickPickButton,
    onChangeCommentData,
    className,
    hasPickButton
  } = t0;
  const userId = useAuthStore(_temp4);
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  let t1;
  if ($[0] !== commentType || $[1] !== contentId || $[2] !== currentPage || $[3] !== isLogin || $[4] !== rootCommentId || $[5] !== sortType) {
    t1 = preventSkipToken((isLogin ? AuthQueryOptions["get:/wello/v2/auth/wello-comment"] : UnauthQueryOptions["get:/wello/v2/allows/wello-comment"])({
      query: {
        page: currentPage,
        commentTypeCd: commentType,
        commentSort: sortType,
        size: SIZE,
        parentCommentId: rootCommentId,
        ...toContentIdQuery(contentId)
      }
    }));
    $[0] = commentType;
    $[1] = contentId;
    $[2] = currentPage;
    $[3] = isLogin;
    $[4] = rootCommentId;
    $[5] = sortType;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = isBot();
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = currentPage === INITIAL_PAGE ? initialPageData : undefined;
  let t4;
  if ($[8] !== userId) {
    t4 = t5 => {
      const {
        context
      } = t5;
      return selectCommentList(context, userId);
    };
    $[8] = userId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      ...t1,
      enabled: !t2,
      initialData: t3,
      staleTime: Number.POSITIVE_INFINITY,
      select: t4,
      placeholderData: keepPreviousData
    };
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const {
    data
  } = useQuery(t5);
  let t6;
  if ($[14] !== data) {
    t6 = data ?? {};
    $[14] = data;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const {
    commentList: t7,
    visibleTotalCount: t8,
    totalRootCount: t9
  } = t6;
  let t10;
  if ($[16] !== t7) {
    t10 = t7 === undefined ? [] : t7;
    $[16] = t7;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const commentList = t10;
  const visibleTotalCount = t8 === undefined ? 0 : t8;
  const totalRootCount = t9 === undefined ? 0 : t9;
  const {
    like
  } = useLike();
  let t11;
  if ($[18] !== like) {
    t11 = (commentId, isLike) => {
      like({
        contentId: commentId,
        isLike,
        likeType: LIKE_TYPE.COMMENT
      });
    };
    $[18] = like;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const handleLike = t11;
  let t12;
  let t13;
  if ($[20] !== data || $[21] !== onChangeCommentData) {
    t12 = () => {
      if (!data) {
        return;
      }
      onChangeCommentData?.(data);
    };
    t13 = [data, onChangeCommentData];
    $[20] = data;
    $[21] = onChangeCommentData;
    $[22] = t12;
    $[23] = t13;
  } else {
    t12 = $[22];
    t13 = $[23];
  }
  useEffect(t12, t13);
  const [previousSortType, setPreviousSortType] = useState(sortType);
  if (previousSortType !== sortType) {
    setPreviousSortType(sortType);
    setCurrentPage(INITIAL_PAGE);
  }
  const [previousPostNonce, setPreviousPostNonce] = useState(postNonce);
  if (previousPostNonce !== postNonce) {
    setPreviousPostNonce(postNonce);
    setCurrentPage(INITIAL_PAGE);
  }
  const isRootComment = !rootCommentId;
  const isInitialChildrenOpened = commentList.length < 50;
  let t14;
  if ($[24] !== className) {
    t14 = cn("bg-Gray-white px-16", className);
    $[24] = className;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== isLogin || $[27] !== onChangeSortType || $[28] !== sortType || $[29] !== visibleTotalCount) {
    t15 = <CommentListHeader isLogin={isLogin} onChangeSortType={onChangeSortType} sortType={sortType} visibleTotalCount={visibleTotalCount} />;
    $[26] = isLogin;
    $[27] = onChangeSortType;
    $[28] = sortType;
    $[29] = visibleTotalCount;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== isLogin) {
    t16 = !isLogin ? <LoginRequiredSection /> : null;
    $[31] = isLogin;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = !isLogin;
  let t18;
  if ($[33] !== t17) {
    t18 = cn("flex flex-col gap-12", {
      "invisible absolute size-0 overflow-hidden": t17
    });
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== commentList || $[36] !== handleLike || $[37] !== hasPickButton || $[38] !== isInitialChildrenOpened || $[39] !== isLogin || $[40] !== onClickEditButton || $[41] !== onClickPickButton || $[42] !== onClickReplyButton || $[43] !== onDeleteSuccess) {
    t19 = <CommentItems commentList={commentList} isLogin={isLogin} hasPickButton={hasPickButton} isInitialChildrenOpened={isInitialChildrenOpened} onDeleteSuccess={onDeleteSuccess} onLike={handleLike} onClickReplyButton={onClickReplyButton} onClickEditButton={onClickEditButton} onClickPickButton={onClickPickButton} />;
    $[35] = commentList;
    $[36] = handleLike;
    $[37] = hasPickButton;
    $[38] = isInitialChildrenOpened;
    $[39] = isLogin;
    $[40] = onClickEditButton;
    $[41] = onClickPickButton;
    $[42] = onClickReplyButton;
    $[43] = onDeleteSuccess;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t18 || $[46] !== t19) {
    t20 = <ul className={t18}>{t19}</ul>;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== currentPage || $[49] !== isRootComment || $[50] !== totalRootCount) {
    t21 = isRootComment ? <div className="mb-32 mt-20 flex justify-center pt-8"><Pagination className="!my-0" currentPage={currentPage} perButtonCount={5} size={SIZE} totalCount={totalRootCount} onMovePage={setCurrentPage} /></div> : null;
    $[48] = currentPage;
    $[49] = isRootComment;
    $[50] = totalRootCount;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== t14 || $[53] !== t15 || $[54] !== t16 || $[55] !== t20 || $[56] !== t21) {
    t22 = <section className={t14} id={SELECTOR.COMMENT_LIST_TOP}>{t15}{t16}{t20}{t21}</section>;
    $[52] = t14;
    $[53] = t15;
    $[54] = t16;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  return t22;
};
const CommentCards = props => {
  const $ = _c(3);
  if (!isEnumValue(COMMENT_SORT_TYPE, props.sortType)) {
    throw new CustomError({
      return_message: "\uB313\uAE00\uC744 \uBD88\uB7EC\uC62C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
    });
  }
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  let t0;
  if ($[0] !== isDesktop || $[1] !== props) {
    t0 = isDesktop ? <CommentCardsPaginated {...props} /> : <CommentCardsInfinite {...props} />;
    $[0] = isDesktop;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export interface CommentListProps extends Omit<CommentCardsProps, 'isLogin'> {
  initialData?: CommentData;
}
export const CommentList = t0 => {
  const $ = _c(7);
  let initialData;
  let restProps;
  if ($[0] !== t0) {
    ({
      initialData,
      ...restProps
    } = t0);
    $[0] = t0;
    $[1] = initialData;
    $[2] = restProps;
  } else {
    initialData = $[1];
    restProps = $[2];
  }
  const isLogin = useAuthStore(_temp5);
  let t1;
  if ($[3] !== initialData || $[4] !== isLogin || $[5] !== restProps) {
    t1 = isLogin !== undefined ? <ErrorBoundary fallback={null}><Suspense fallback={<CommentSkeletons initialData={initialData} />}><CommentCards {...restProps} isLogin={isLogin} /></Suspense></ErrorBoundary> : <CommentSkeletons initialData={initialData} />;
    $[3] = initialData;
    $[4] = isLogin;
    $[5] = restProps;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
function _temp(state) {
  return state.myInfo?.id;
}
function _temp2(t0) {
  const {
    context
  } = t0;
  const {
    rootTotalCount: root_total_count,
    page
  } = context ?? {};
  if (!root_total_count || !page) {
    return;
  }
  const nextPage = page + 1;
  const maxPage = Math.ceil(root_total_count / SIZE);
  if (maxPage < nextPage) {
    return;
  }
  return nextPage;
}
function _temp3(comment, index, self) {
  return index === self.findIndex(t => t.commentId === comment.commentId);
}
function _temp4(state) {
  return state.myInfo?.id;
}
function _temp5(state) {
  return state.isLogin;
}